/**
 * Class representing the structure of a question part.
 * This class handles initialization, data management, and various operations related to question parts.
 */
class QuestionPartStructure {
    /**
     * Creates an instance of QuestionPartStructure.
     * @param {Object} $uibModal - Angular UI Bootstrap modal service.
     * @param {Object} $translate - Angular translate service.
     * @param {Object} $timeout - Angular timeout service.
     * @param {Object} sheetStructure - The structure of the sheet containing question parts.
     * @param {Object} data - Initial data for the question part.
     * @param {Object} defaultQuestionItemGroup - Default question item group.
     */
    constructor($uibModal, $translate, $timeout, sheetStructure, data, defaultQuestionItemGroup) {
        this.$uibModal = $uibModal;
        this.$translate = $translate;
        this.$timeout = $timeout;
        this.sheetStructure = sheetStructure;
        this.data = data;
        this.defaultQuestionItemGroup = defaultQuestionItemGroup;
        this._alphabetObj = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z"];
        this.questionPartPrefix = $translate.instant('PREFIX.QUESTION-PART');
        this.inUse = [];
        this.init();
    }

    /**
     * Initializes the question part structure.
     * If no data is provided, initializes with default data.
     * @returns {QuestionPartStructure} - The current instance of QuestionPartStructure.
     */
    init() {
        if (typeof this.data === "undefined" || this.data === null) {
            this.data = this.initData();
        }
        return this;
    }

    /**
     * Initializes default data for a question part.
     * @returns {Object} - Default data object for a question part.
     */
    initData() {
        return {
            status: 0,
            bookmarked: false,
            uuid: angular.createUUID(),
            title: null,
            shortTitle: this.getFreeAlphabet(this.sheetStructure),
            attachement: {},
            marks: 0,
            type: "questionPart",
            ibisID: null,
            useCustomTitle: false,
            isDefaultQuestionPart: false,
            questionItemGroup: this.defaultQuestionItemGroup,
            criterionMarks: []
        };
    }

    /**
     * Finds the first available alphabet letter that is not in use by other question parts.
     * @param {Object} sheetStructure - The structure of the sheet containing question parts.
     * @returns {string|null} - The first available alphabet letter or null if all are in use.
     */
    getFreeAlphabet(sheetStructure) {
        let alphabet = null;
        angular.forEach(this._alphabetObj, (value, key) => {
            if (alphabet === null) {
                let find = false;
                angular.forEach(sheetStructure.questionParts, (questionPartValue, questionPartKey) => {
                    if (value === questionPartValue.data.shortTitle) {
                        find = true;
                    }
                });
                if (!find) {
                    alphabet = value;
                    return false;
                }
            } else {
                return false;
            }
        });
        return alphabet;
    }

    /**
     * Gets the prefix for the question part.
     * @returns {string} - The prefix for the question part.
     */
    getPrefix() {
        return this.questionPartPrefix;
    }

    /**
     * Checks if a short title already exists in the sheet structure.
     * @param {string} data - The short title to check.
     * @returns {boolean} - True if the short title exists, false otherwise.
     */
    sortTitleExists(data) {
        let exists = false;
        angular.forEach(this.sheetStructure.questionParts, (questionPartValue, questionPartKey) => {
            if (questionPartValue.data.shortTitle === data && questionPartValue.data.uuid !== this.data.uuid) {
                exists = true;
            }
        });
        return exists;
    }

    /**
     * Validates the short title before saving.
     * @param {string} data - The short title to validate.
     * @returns {string|undefined} - Warning message if validation fails, undefined otherwise.
     */
    onBeforeSaveShortTitle(data) {
        if (!data) {
            return this.$translate.instant('CMS.SHEET-STRUCTURE-EDITOR.WARNING.QUESTION-TITLE-EMPTY');
        } else if (this.sortTitleExists(data)) {
            return this.$translate.instant('CMS.SHEET-STRUCTURE-EDITOR.WARNING.QUESTION-PART-SHORT-TITLE-EXISTS');
        }
    }

    /**
     * Validates the marks input.
     * @param {number} data - The marks to validate.
     * @returns {string|undefined} - Warning message if validation fails, undefined otherwise.
     */
    validateMarks(data) {
        let marks = parseInt(Number(data), 10);
        if (isNaN(marks)) {
            return this.$translate.instant('CMS.SHEET-STRUCTURE-EDITOR.WARNING.MARKS-NUMBER');
        } else if (marks < 0) {
            return this.$translate.instant('CMS.SHEET-STRUCTURE-EDITOR.WARNING.MARKS-MIN');
        } else if (marks > 999) {
            return this.$translate.instant('CMS.SHEET-STRUCTURE-EDITOR.WARNING.MARKS-MAX');
        } else {
            this.setMarks(marks);
        }
    }

    /**
     * Sets the marks for the question part.
     * @param {number} data - The marks to set.
     */
    setMarks(data) {
        this.data.marks = data;
    }

    /**
     * Destroys the question part by removing it from the sheet structure.
     */
    destroy() {
        angular.forEach(this.sheetStructure.questionParts, (questionPart, key) => {
            if (questionPart.data.uuid === this.data.uuid) {
                this.sheetStructure.questionParts.splice(key, 1);
            }
        });
    }

    /**
     * Opens a modal to confirm deletion of the question part.
     */
    onDeleteQuestionPart() {
        this.inUseQuestionParts();

        let modalInstance = this.$uibModal.open({
            templateUrl: "/views/ib/cms/sheetStructure/deleteQuestionPartModal.html",
            windowClass: "delete-question-part",
            transclude: false,
            backdrop: 'static',
            keyboard: false,
            controller: 'DeleteQuestionPartModalController',
            resolve: {
                questionPart: () => this,
                sheetStructure: () => this.sheetStructure
            }
        });

        modalInstance.opened.then(() => {
            this.$timeout(() => {
                $("#question-parts-list").trigger('change');
            }, 10);
        });
    }

    /**
     * Moves the question part up in the list.
     * @param {number} index - The current index of the question part.
     * @returns {boolean} - False if the question part is already at the top.
     */
    onQuestionPartUp(index) {
        if (index === 0) {
            return false;
        }

        let newIndex = index - 1;
        let clone = angular.copy(this.sheetStructure.questionParts[index]);

        this.sheetStructure.questionParts.splice(index, 1);
        this.sheetStructure.questionParts.splice(newIndex, 0, clone);

        this.$timeout(() => {
            $(".question-item-groups-select").trigger('change');
        });
    }

    /**
     * Moves the question part down in the list.
     * @param {number} index - The current index of the question part.
     * @returns {boolean} - False if the question part is already at the bottom.
     */
    onQuestionPartDown(index) {
        if (index === this.sheetStructure.questionParts.length - 1) {
            return false;
        }

        let newIndex = index + 1;
        let clone = angular.copy(this.sheetStructure.questionParts[index]);

        this.sheetStructure.questionParts.splice(index, 1);
        this.sheetStructure.questionParts.splice(newIndex, 0, clone);

        this.$timeout(() => {
            $(".question-item-groups-select").trigger('change');
        });
    }

    /**
     * Checks if the question part is in use in the sheet structure layout.
     */
    inUseQuestionParts() {
        if (!this.sheetStructure.isNewSheet) {
            if (typeof this.sheetStructure.layout !== "undefined") {
                this.inUse = [];
                angular.forEach(this.sheetStructure.layout.rows, (row, rowKey) => {
                    angular.forEach(row.cols, (col, colKey) => {
                        angular.forEach(col.list, (list, listKey) => {
                            if (list.content.data.questionPart === this.data.uuid) {
                                let obj = {
                                    contentUUID: angular.copy(list.content.uuid),
                                    contentObj: angular.copy(list.content),
                                    rowKey: rowKey,
                                    colKey: colKey,
                                    listKey: listKey
                                };
                                this.inUse.push(obj);
                            }
                        });
                    });
                });
            }
        }
    }
}

export default QuestionPartStructure;